import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ArticleHero from "@/components/ArticleHero";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { webPageSchema } from "@/components/schema";

const PATH = "/editorial-standards";
const TITLE = "Editorial standards";
const DESCRIPTION =
  "The public, named standards Spain Smile Guide holds itself to — the rubric, the corrections policy, the disclosure, and the list of things we will not do.";

export default function EditorialStandards() {
  return (
    <>
      <SEO
        title={TITLE}
        description={DESCRIPTION}
        path={PATH}
        type="website"
        jsonLd={webPageSchema(TITLE, DESCRIPTION, PATH)}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", path: "/" },
          { name: "Editorial standards", path: PATH },
        ]}
      />

      <ArticleHero
        kicker="Publication"
        title="Editorial standards"
        standfirst="The rules that keep this publication credible — published in plain language, so a reader can hold us to them."
        datePublished="2026-04-18"
        dateModified="2026-04-18"
      />

      <div className="container-prose prose-editorial mt-10">
        <p>
          Spain Smile Guide covers a category in which most publications
          are either the clinics themselves or the platforms that charge
          the clinics for placement. We are neither. The standards below
          are the ones we work to, and the ones readers are entitled to
          hold us to. None of this is aspiration — every commitment on
          this page is already operational, and the supporting pages it
          links to are live.
        </p>

        <h2>The three documents this page links to</h2>
        <p>
          Three pages on this site together constitute the publication's
          public standards. Each is maintained; each is dated.
        </p>
        <ul>
          <li>
            <strong>
              <Link to="/methodology">Methodology</Link>
            </strong>{" "}
            — the twelve-dimension rubric we apply to every clinic we
            evaluate, published in full, dated, with a revision log at its
            foot.
          </li>
          <li>
            <strong>
              <Link to="/corrections">Corrections</Link>
            </strong>{" "}
            — a chronological, public log of every factual correction
            made to a piece on this site. Substantive corrections are
            also surfaced at the top of the affected article for one
            month after they are made.
          </li>
          <li>
            <strong>
              <Link to="/about">About</Link>
            </strong>{" "}
            — the publication's editorial disclosure, the identity of
            the team behind it, the affiliation with the ACE DNTL STUDIO
            group, and the funding model that makes the work possible.
          </li>
        </ul>
        <p>
          Readers sceptical of any specific claim in any piece should
          begin on one of those three pages. They are the infrastructure
          under the writing.
        </p>

        <h2>What we will not do</h2>
        <p>
          Any one of the following is a reason to kill a piece before it
          publishes. These are drawn from our internal governance
          document; we surface them here in the voice a reader can use.
        </p>
        <ol>
          <li>
            <strong>We will not let the writing sound like marketing.</strong>{" "}
            The editorial voice on this site is analytical, long-form, and
            occasionally dry. If a piece begins to read like a clinic's
            own copy — ACE DNTL's included — it is rewritten or it is not
            published.
          </li>
          <li>
            <strong>
              We will not let any single clinic dominate the writing.
            </strong>{" "}
            ACE DNTL is mentioned at most twice in any article that
            mentions it at all, and not at all in rubric-teaching pieces
            where the subject is how to evaluate rather than who to
            choose. The same discipline applies to every other clinic we
            cover.
          </li>
          <li>
            <strong>We will not rank clinics.</strong> No numeric
            positions. No "best in Marbella" ordering. Comparison pieces
            describe where each practice leads and where it trails, on
            the same dimensions, in the same order. The reader does the
            ranking.
          </li>
          <li>
            <strong>We will not publish thin content.</strong> Every
            piece earns its length. Comparison and reference pieces on
            this site sit above 1,500 words as a working minimum; shorter
            pieces have to clear a specific bar for insight the length
            cannot carry.
          </li>
          <li>
            <strong>
              We will not cover only the clinics affiliated with us.
            </strong>{" "}
            Market pieces name at least five clinics in the market they
            survey. Where a region has fewer than that operating at a
            level worth covering, we say so in the piece rather than
            padding the list.
          </li>
          <li>
            <strong>We will not publish without the trust stack.</strong>{" "}
            Every piece carries a byline, a date, a last-reviewed date, a
            link to the methodology, and — where it mentions ACE DNTL —
            the affiliation disclosure. Pages that lack any of those are
            incomplete, and we treat them as incomplete.
          </li>
          <li>
            <strong>We will not overlink to ACE DNTL.</strong> At most one
            outbound link to acedntl.com per article. Links to competing
            clinics are the norm on comparison pieces, not the exception.
          </li>
          <li>
            <strong>
              We will not tilt the rubric to preserve a favourable outcome.
            </strong>{" "}
            The rubric is the rubric. If it stops favouring the clinic
            we are affiliated with, the coverage reflects that and the
            rubric stays. The moment we change the rubric to preserve a
            result, every other piece of writing on this site becomes
            worth less.
          </li>
        </ol>

        <h2>Two commitments a reader can test</h2>
        <p>
          Two operational commitments in particular are worth calling out,
          because they are the ones easiest for a reader to verify.
        </p>
        <p>
          <strong>The meta-test.</strong> Before any article on this site
          goes live, an editor strips every mention of ACE DNTL, Dr. Ace
          Korkchi, and the ACE Smile Index&trade; from the draft. If the remainder
          still delivers useful, specific, non-obvious guidance to a
          patient choosing a clinic, the piece is published. If not, it
          is rewritten — or killed. A reader with a text editor and ten
          minutes can run the same test. The result should be the same.
        </p>
        <p>
          <strong>One correction a quarter.</strong> A publication at this
          scale, covering a live market, should accumulate at least one
          visible correction every three months. A corrections log with
          zero entries after a year of publication is a warning, not a
          reassurance. We expect the log to grow, and we will resist the
          temptation to edit silently.
        </p>

        <h2>How to hold us to this</h2>
        <p>
          If a piece on this site appears to breach any of the commitments
          above — if a clinic is mentioned more often than the caps
          allow, if an article reads as marketing, if a correction
          should have been logged and was not — write to the editorial
          desk via the address on our{" "}
          <Link to="/contact">contact page</Link>. Complaints about
          specific pieces are prioritised over general feedback.
          Substantive complaints that result in a change to the piece
          are recorded on the{" "}
          <Link to="/corrections">corrections page</Link>.
        </p>

        <hr className="rule" />
        <p className="text-small text-charcoal/70">
          The standards on this page are editorial policy and cannot be
          weakened without a dated change to this page.
        </p>
      </div>
    </>
  );
}
